<script setup lang="ts">
/**
 * AuthLayout — unauthenticated screens.
 *
 * Centred card on the warm-ivory page background, brand mark at the
 * top, no sidebar/topbar. Used for sign-in, sign-up, forgot-password,
 * and any future onboarding flow.
 */
import { RouterView } from 'vue-router'
import {
  ToastStack,
  ConfirmModal,
  ErrorBoundary,
  LoadingSkeleton,
  LocaleSwitch,
} from '@/components/ui'
import logoUrl from '@/assets/logo/lexentiqa-gold.svg'
</script>

<template>
  <div class="auth-shell">
    <header class="auth-shell__brand">
      <img class="auth-shell__logo" :src="logoUrl" alt="Lexentiqa" />
    </header>

    <main class="auth-shell__main">
      <ErrorBoundary>
        <Suspense>
          <RouterView v-slot="{ Component, route }">
            <component :is="Component" :key="route.path" />
          </RouterView>
          <template #fallback>
            <LoadingSkeleton variant="block" height="320px" />
          </template>
        </Suspense>
      </ErrorBoundary>
    </main>

    <footer class="auth-shell__foot">
      <span>© Lexentiqa · AI Trademark Monitoring</span>
      <span class="auth-shell__sep">·</span>
      <a href="#">Terms</a>
      <a href="#">Privacy</a>
      <span class="auth-shell__sep">·</span>
      <!-- Before the login, not after it: a user who cannot read this page
           has no route to Settings to change the language. -->
      <LocaleSwitch variant="inline" />
    </footer>

    <ToastStack />
    <ConfirmModal />
  </div>
</template>

<style scoped>
.auth-shell {
  min-height: 100vh;
  display: grid;
  /* minmax(0, …): a single auto column took the footer's one-line width, so
   * every auth screen was 523px wide on a 375px phone. */
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto 1fr auto;
  background:
    radial-gradient(1200px 600px at 80% -10%, var(--color-gold-faint), transparent 60%),
    radial-gradient(900px 500px at -10% 110%, var(--color-sage-soft), transparent 60%),
    var(--color-bg-page);
}

.auth-shell__brand {
  padding: var(--space-7) var(--space-7) 0;
  display: flex;
  align-items: center;
}
.auth-shell__logo {
  height: 28px;
  width: auto;
  filter: drop-shadow(0 1px 1px rgba(31, 35, 37, 0.04));
}

.auth-shell__main {
  display: grid;
  place-items: center;
  padding: var(--space-7) var(--space-5);
}

.auth-shell__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  justify-content: center;
  padding: var(--space-5);
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
}
.auth-shell__foot a {
  color: var(--color-text-secondary);
  text-decoration: none;
}
.auth-shell__foot a:hover {
  color: var(--color-text);
}
.auth-shell__sep {
  opacity: 0.5;
}

@media (max-width: 640px) {
  .auth-shell__brand {
    padding: var(--space-5) var(--space-5) 0;
  }
  .auth-shell__main {
    padding: var(--space-5) var(--space-4);
  }
}
</style>
